import { useEffect, useRef, useState } from 'react'
import styles from './ProfessorProjectPageContent.module.less'
import { usePagination } from 'alova/client'
import { api } from '@/api'
import { useInViewport } from 'ahooks'
import { Spin } from 'antd'
import { commonUtils } from '@/helpers/commonUtils'
import { useTranslation } from 'react-i18next'
import ProjectCard from '@/pages/ProjectPage/components/ProjectCard/ProjectCard'
import ASearch from '@/components/A/ASearch/ASearch'

const PAGE_SIZE = 20
const useListStatus = (total: number | undefined, isLastPage: boolean) => {
  const [status, setStatus] = useState<'empty' | 'hasMore' | 'noMore'>('hasMore')

  useEffect(() => {
    setStatus(!total ? 'empty' : isLastPage ? 'noMore' : 'hasMore')
  }, [total, isLastPage])

  return status
}

function ProfessorProjectDetailContent() {
  const { t } = useTranslation()
  const [keyword, setKeyword] = useState('')
  const {
    data: listData,
    send: sendListRequest,
    abort: abortListRequest,
    update: updateListData,
    reload: reloadList,
    page,
    total,
    isLastPage,
    loading,
  } = usePagination(
    (pageNo, pageSize) => {
      return api.expert.projectList({ pageNo, pageSize, keyword })
    },
    {
      initialPage: 1,
      initialPageSize: PAGE_SIZE,
      // initialData: [],
      data: (res) => {
        return res.objects.map((item) => ({
          ...item,
          // 将接口返回的其他字段放入 listData 中
          waitCount: res.waitCount,
        }))
      },
      total: (res) => res.totalCount,
      append: true,
      immediate: false,
      preloadNextPage: false,
      preloadPreviousPage: false,
    }
  )

  const listStatus = useListStatus(total, isLastPage)

  const loadingRef = useRef<HTMLDivElement>(null)
  const [spinInView] = useInViewport(loadingRef)

  useEffect(() => {
    sendListRequest(1, PAGE_SIZE)
    return () => {
      abortListRequest()
    }
  }, [])

  useEffect(() => {
    if (spinInView) {
      updateListData({ page: page + 1 })
    }
  }, [spinInView])

  return (
    <div className={styles.professorProjectPageContent} data-testid='professor-project-page'>
      <div className={styles.topBox}>
        <span>
          {t('professor.waitRateCount')}: {total === 0 ? 0 : listData[0]?.waitCount}/{total}
        </span>
        <ASearch
          testId='professor-project-search-input'
          className={styles.searchInput}
          placeholder={t('dashboard.projectPage.searchPlaceholder')}
          allowClear
          onSearch={(value) => {
            const keyword = value ? `${value}` : ''
            setKeyword(keyword)
            reloadList()
          }}
        ></ASearch>
      </div>
      <div className={styles.contentBox}>
        {page === 1 && loading ? (
          <div className={styles.loadingBox}>
            <Spin />
          </div>
        ) : (
          <>
            {listStatus === 'empty' && (
              <div className={styles.emptyBox}>
                <img src={commonUtils.getImagePath('projectPage/project-list-empty.png')} alt='' />
                <p>{t('dashboard.projectPage.noRelevantContent')}</p>
              </div>
            )}
            {listStatus !== 'empty' && (
              <>
                <ProjectCardList projectItemList={listData} />
                {listStatus === 'hasMore' && listData.length > 0 ? (
                  <div className={styles.loadingBox} ref={loadingRef}>
                    <Spin />
                  </div>
                ) : null}
              </>
            )}
          </>
        )}
      </div>
    </div>
  )
}

const ProjectCardList = (props: { projectItemList: ProjectModels.ProjectListItem[] }) => {
  return (
    <div className={styles.cardList}>
      {props.projectItemList.map((item, index) => {
        return (
          <div className={styles.cardItem} key={item.id}>
            <ProjectCard mode='professor' item={item} prioritizeCover={index < 6} />
          </div>
        )
      })}
    </div>
  )
}

export default ProfessorProjectDetailContent
